import { useState, useEffect, useCallback, useRef } from 'react'
import { Table, Button, Modal, Form, Badge, InputGroup, Tabs, Tab, Row, Col, Card, Dropdown, ButtonGroup } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { extensionsApi, trunksApi, softphoneApi } from '../services/api'
import { useAuthStore } from '../store/authStore'
import { useConfirm } from '../components/common/ConfirmDialog'

interface Extension {
  id: string
  extension: string
  name: string
  password: string
  caller_id: string | null
  is_active: boolean
  tenant_id?: string
  tenant_domain?: string  // For LDAP configuration
  voicemail_enabled: boolean
  voicemail_pin: string | null
  mwi_mailbox: string | null
  voicemail_mailboxes: string[]
  hint_enabled: boolean
  show_in_phonebook: boolean
  language: string
  codecs: string
  call_recording: boolean
  call_waiting: boolean
  dnd_enabled: boolean
  // Sprachverschluesselung (SRTP) Geraet <-> Anlage
  media_encryption: 'off' | 'optional' | 'mandatory'
  // Multi-Device / SIP
  max_contacts: number
  device_state_busy_at: number
  // Call Group / Pickup Group
  call_group: number
  pickup_group: number
  // Forwarding
  forward_unconditional: string | null
  forward_unconditional_enabled: boolean
  forward_busy: string | null
  forward_busy_enabled: boolean
  forward_busy_internal: string | null
  forward_busy_external: string | null
  forward_noanswer: string | null
  forward_noanswer_enabled: boolean
  forward_noanswer_timeout: number
  forward_noanswer_internal: string | null
  forward_noanswer_external: string | null
  forward_unavailable: string | null
  forward_unavailable_enabled: boolean
  // Busy behavior
  busy_signal_internal: boolean
  busy_signal_external: boolean
  // Outgoing CID
  outgoing_cid_default: string | null
  outgoing_cid_rules: string | null
  // Ring
  ring_timeout: number
  call_screening: boolean
  // Voicemail advanced
  voicemail_greeting_enabled: boolean
  voicemail_busy_enabled: boolean
  voicemail_unavail_enabled: boolean
  voicemail_trunk_only: boolean
  voicemail_trunk_ids: string | null
  voicemail_email: string | null
  voicemail_email_attach: boolean
  voicemail_delete_after_email: boolean
  voicemail_announce_cid: boolean
  notes: string | null
  // Live AstDB forwarding state (from Asterisk, may differ from GUI DB)
  live_cf: string | null
  live_cfb: string | null
  live_cfna: string | null
  // SIP registration status
  reg_status: boolean | null
  reg_ip: string | null
  reg_public_ip: string | null
  reg_user_agent: string | null
  reg_contact_count: number | null
}

interface Trunk {
  id: string
  name: string
}

// FastAPI liefert `detail` entweder als Text (HTTPException) oder als Liste von
// Validierungsfehlern. Bisher verschluckte die Seite beides und zeigte nur
// "Fehler beim Speichern" — ohne jeden Hinweis, welches Feld schuld war.
function apiErrorMessage(err: unknown, fallback: string): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
  if (typeof detail === 'string' && detail) return detail
  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0] as { loc?: unknown[]; msg?: string }
    const field = Array.isArray(first.loc) ? first.loc.filter(p => p !== 'body').join('.') : ''
    return field ? `${field}: ${first.msg}` : (first.msg || fallback)
  }
  return fallback
}

const defaultFormData: Partial<Extension> & { extension: string; name: string } = {
  extension: '',
  name: '',
  password: '',
  caller_id: '',
  is_active: true,
  voicemail_enabled: false,
  voicemail_pin: '',
  mwi_mailbox: '',
  voicemail_mailboxes: [],
  call_recording: false,
  call_waiting: false,
  media_encryption: 'off',
  hint_enabled: true,
  show_in_phonebook: true,
  language: 'en',
  codecs: 'g722,ulaw,alaw',
  dnd_enabled: false,
  // Multi-Device / SIP
  max_contacts: 1,
  device_state_busy_at: 1,
  // Call Group / Pickup Group
  call_group: 1,
  pickup_group: 1,
  // Forwarding
  forward_unconditional: '',
  forward_unconditional_enabled: false,
  forward_busy: '',
  forward_busy_enabled: false,
  forward_busy_internal: '',
  forward_busy_external: '',
  forward_noanswer: '',
  forward_noanswer_enabled: false,
  forward_noanswer_timeout: 20,
  forward_noanswer_internal: '',
  forward_noanswer_external: '',
  forward_unavailable: '',
  forward_unavailable_enabled: false,
  // Busy behavior
  busy_signal_internal: true,
  busy_signal_external: true,
  // Outgoing CID
  outgoing_cid_default: '',
  outgoing_cid_rules: '[]',
  // Ring
  ring_timeout: 30,
  call_screening: false,
  // Voicemail
  voicemail_greeting_enabled: true,
  voicemail_busy_enabled: true,
  voicemail_unavail_enabled: true,
  voicemail_trunk_only: false,
  voicemail_trunk_ids: '[]',
  voicemail_email: '',
  voicemail_email_attach: true,
  voicemail_delete_after_email: false,
  voicemail_announce_cid: true,
  notes: ''
}

export default function Extensions() {
  const [extensions, setExtensions] = useState<Extension[]>([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [showCloneModal, setShowCloneModal] = useState(false)
  const [showQrModal, setShowQrModal] = useState(false)
  const [editingExt, setEditingExt] = useState<Extension | null>(null)
  const [cloningExt, setCloningExt] = useState<Extension | null>(null)
  const [qrExt, setQrExt] = useState<Extension | null>(null)
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('')
  const [qrLoading, setQrLoading] = useState(false)
  const [search, setSearch] = useState('')
  const { selectedTenant, user } = useAuthStore()
  const isSuperAdmin = user?.role === 'super_admin'
  const confirm = useConfirm()

  const isPollingRef = useRef(false)

  const loadExtensions = useCallback(async (isPolling = false) => {
    try {
      const params = isSuperAdmin && selectedTenant ? { tenant_id: selectedTenant.id } : {}
      const response = await extensionsApi.list(params)
      setExtensions(response.data.items || [])
    } catch {
      if (!isPolling) {
        toast.error('Fehler beim Laden der Nebenstellen')
      }
    } finally {
      if (!isPolling) {
        setLoading(false)
      }
    }
  }, [isSuperAdmin, selectedTenant])

  useEffect(() => {
    setLoading(true)
    loadExtensions()
  }, [selectedTenant, loadExtensions])

  // Poll registration status every 10 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (!isPollingRef.current) {
        isPollingRef.current = true
        loadExtensions(true).finally(() => { isPollingRef.current = false })
      }
    }, 10000)
    return () => clearInterval(interval)
  }, [loadExtensions])

  const handleDelete = async (id: string) => {
    const confirmed = await confirm({
      title: 'Nebenstelle löschen',
      message: 'Nebenstelle wirklich löschen?',
      confirmText: 'Löschen',
      variant: 'danger'
    })
    if (!confirmed) return
    try {
      await extensionsApi.delete(id)
      toast.success('Nebenstelle gelöscht')
      loadExtensions()
    } catch {
      toast.error('Fehler beim Löschen')
    }
  }

  const handleToggleDnd = async (id: string, enabled: boolean) => {
    try {
      await extensionsApi.toggleDnd(id, enabled)
      // Update local state immediately for fast UI response
      setExtensions(prev => prev.map(ext =>
        ext.id === id ? { ...ext, dnd_enabled: enabled } : ext
      ))
      toast.success(enabled ? 'DND aktiviert' : 'DND deaktiviert')
    } catch {
      toast.error('Fehler beim Ändern des DND-Status')
    }
  }

  const handleSave = async (data: Record<string, unknown>) => {
    try {
      if (editingExt) {
        await extensionsApi.update(editingExt.id, data)
        toast.success('Nebenstelle aktualisiert')
      } else {
        // Super admin must select a tenant first
        if (isSuperAdmin && !selectedTenant) {
          toast.error('Bitte wählen Sie zuerst einen Mandanten aus')
          return
        }
        const tenantId = isSuperAdmin ? selectedTenant?.id : undefined
        await extensionsApi.create(data, tenantId)
        toast.success('Nebenstelle erstellt')
      }
      setShowModal(false)
      loadExtensions()
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Fehler beim Speichern'))
    }
  }

  const handleClone = async (data: { new_extension: string; new_name: string; new_password?: string }) => {
    if (!cloningExt) return
    try {
      await extensionsApi.clone(cloningExt.id, data)
      toast.success(`Nebenstelle ${data.new_extension} erstellt`)
      setShowCloneModal(false)
      loadExtensions()
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Fehler beim Klonen'))
    }
  }

  const handleShowQrCode = async (ext: Extension) => {
    setQrExt(ext)
    setQrLoading(true)
    setShowQrModal(true)
    try {
      const response = await softphoneApi.generateProvisionToken(ext.id, 10)
      const token = response.data.token
      setQrCodeUrl(softphoneApi.getProvisionQrUrl(ext.id, token))
    } catch {
      toast.error('Fehler beim Generieren des QR-Codes')
      setShowQrModal(false)
    } finally {
      setQrLoading(false)
    }
  }

  const handleSendQrEmail = async (ext: Extension) => {
    try {
      const response = await softphoneApi.sendProvisionEmail(ext.id, 30)
      toast.success(response.data.message)
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } }
      toast.error(error.response?.data?.detail || 'E-Mail-Versand fehlgeschlagen')
    }
  }

  const filteredExtensions = extensions.filter(ext =>
    ext.extension.includes(search) || ext.name.toLowerCase().includes(search.toLowerCase())
  )

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4>Nebenstellen</h4>
        <div className="d-flex gap-2">
          <InputGroup style={{ width: '250px' }}>
            <InputGroup.Text><i className="bi bi-search"></i></InputGroup.Text>
            <Form.Control
              placeholder="Suchen..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </InputGroup>
          <Button variant="primary" onClick={() => { setEditingExt(null); setShowModal(true); }}>
            <i className="bi bi-plus-lg me-1"></i> Neue Nebenstelle
          </Button>
        </div>
      </div>

      <div className="table-container">
        <Table hover className="mb-0">
          <thead>
            <tr>
              <th>Nebenstelle</th>
              <th>Name</th>
              <th>Status</th>
              <th>Registrierung</th>
              <th>Voicemail</th>
              <th>Weiterl.</th>
              <th>BLF</th>
              <th className="text-end">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="text-center py-4">Laden...</td></tr>
            ) : filteredExtensions.length === 0 ? (
              <tr><td colSpan={8} className="text-center py-4 text-muted">Keine Nebenstellen gefunden</td></tr>
            ) : (
              filteredExtensions.map(ext => (
                <tr key={ext.id}>
                  <td><strong>{ext.extension}</strong></td>
                  <td>{ext.name}</td>
                  <td>
                    <Badge bg={ext.is_active ? 'success' : 'secondary'}>
                      {ext.is_active ? 'Aktiv' : 'Inaktiv'}
                    </Badge>
                    <Badge
                      bg={ext.dnd_enabled ? 'warning' : 'light'}
                      text={ext.dnd_enabled ? undefined : 'muted'}
                      className="ms-1"
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleToggleDnd(ext.id, !ext.dnd_enabled)}
                      title={ext.dnd_enabled ? 'DND deaktivieren' : 'DND aktivieren'}
                    >
                      <i className={`bi ${ext.dnd_enabled ? 'bi-bell-slash-fill' : 'bi-bell'}`}></i>
                    </Badge>
                  </td>
                  <td>
                    {ext.reg_status === true ? (
                      <Badge
                        bg="success"
                        title={[
                          ext.reg_ip && `IP: ${ext.reg_ip}`,
                          ext.reg_public_ip && ext.reg_public_ip !== ext.reg_ip && `Öffentlich: ${ext.reg_public_ip}`,
                          ext.reg_user_agent && ext.reg_user_agent,
                          ext.reg_contact_count && ext.reg_contact_count > 1 && `${ext.reg_contact_count} Geräte`
                        ].filter(Boolean).join('\n')}
                      >
                        <i className="bi bi-wifi me-1"></i>Online{ext.reg_contact_count && ext.reg_contact_count > 1 ? ` (${ext.reg_contact_count})` : ''}
                      </Badge>
                    ) : ext.reg_status === false ? (
                      <Badge bg="danger">
                        <i className="bi bi-wifi-off me-1"></i>Offline
                      </Badge>
                    ) : (
                      <Badge bg="light" text="muted">?</Badge>
                    )}
                  </td>
                  <td>{ext.voicemail_enabled ? <i className="bi bi-check-circle text-success"></i> : <i className="bi bi-x-circle text-muted"></i>}</td>
                  <td>
                    {ext.live_cf ? (
                      <Badge
                        bg={ext.forward_unconditional_enabled ? 'info' : 'warning'}
                        title={`Immer → ${ext.live_cf}${!ext.forward_unconditional_enabled ? ' (via Telefon)' : ''}`}
                      >
                        Immer{!ext.forward_unconditional_enabled && <i className="bi bi-telephone-fill ms-1" style={{fontSize: '0.65rem'}}></i>}
                      </Badge>
                    ) : ext.forward_unconditional_enabled && (
                      <Badge bg="info" className="opacity-50" title="Immer (GUI, nicht aktiv in Asterisk)">Immer?</Badge>
                    )}
                    {ext.live_cfb ? (
                      <Badge
                        bg={ext.forward_busy_enabled ? 'secondary' : 'warning'}
                        className="ms-1"
                        title={`Besetzt → ${ext.live_cfb}${!ext.forward_busy_enabled ? ' (via Telefon)' : ''}`}
                      >
                        Bes.{!ext.forward_busy_enabled && <i className="bi bi-telephone-fill ms-1" style={{fontSize: '0.65rem'}}></i>}
                      </Badge>
                    ) : ext.forward_busy_enabled && (
                      <Badge bg="secondary" className="ms-1 opacity-50" title="Besetzt (GUI, nicht aktiv in Asterisk)">Bes.?</Badge>
                    )}
                    {ext.live_cfna ? (
                      <Badge
                        bg={ext.forward_noanswer_enabled ? 'secondary' : 'warning'}
                        className="ms-1"
                        title={`Keine Antwort → ${ext.live_cfna}${!ext.forward_noanswer_enabled ? ' (via Telefon)' : ''}`}
                      >
                        K.A.{!ext.forward_noanswer_enabled && <i className="bi bi-telephone-fill ms-1" style={{fontSize: '0.65rem'}}></i>}
                      </Badge>
                    ) : ext.forward_noanswer_enabled && (
                      <Badge bg="secondary" className="ms-1 opacity-50" title="Keine Antwort (GUI, nicht aktiv in Asterisk)">K.A.?</Badge>
                    )}
                  </td>
                  <td>{ext.hint_enabled ? <i className="bi bi-check-circle text-success"></i> : <i className="bi bi-x-circle text-muted"></i>}</td>
                  <td className="text-end">
                    <Dropdown as={ButtonGroup} size="sm" className="me-1">
                      <Dropdown.Toggle variant="outline-success" title="Softphone herunterladen">
                        <i className="bi bi-telephone-download"></i>
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        <Dropdown.Header>Softphone Download</Dropdown.Header>
                        <Dropdown.Item onClick={() => {
                          toast.info('Download wird gestartet...')
                          softphoneApi.download('windows').catch(() => toast.error('Download fehlgeschlagen'))
                        }}>
                          <i className="bi bi-windows me-2"></i>Windows
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => {
                          toast.info('Download wird gestartet...')
                          softphoneApi.download('linux-deb').catch(() => toast.error('Download fehlgeschlagen'))
                        }}>
                          <i className="bi bi-ubuntu me-2"></i>Linux (.deb)
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => {
                          toast.info('Download wird gestartet...')
                          softphoneApi.download('linux').catch(() => toast.error('Download fehlgeschlagen'))
                        }}>
                          <i className="bi bi-ubuntu me-2"></i>Linux (AppImage)
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => {
                          toast.info('Download wird gestartet...')
                          softphoneApi.download('android').catch(() => toast.error('Download fehlgeschlagen'))
                        }}>
                          <i className="bi bi-android2 me-2"></i>Android
                        </Dropdown.Item>
                        <Dropdown.Divider />
                        <Dropdown.Header>Konfiguration</Dropdown.Header>
                        <Dropdown.Item onClick={() => {
                          softphoneApi.downloadConfig(ext.id).catch(() => toast.error('Download fehlgeschlagen'))
                        }}>
                          <i className="bi bi-file-earmark-code me-2"></i>Config herunterladen
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => handleShowQrCode(ext)}>
                          <i className="bi bi-qr-code me-2"></i>QR-Code anzeigen
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => handleSendQrEmail(ext)}>
                          <i className="bi bi-envelope me-2"></i>QR per E-Mail senden
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                    <Button variant="outline-secondary" size="sm" className="me-1" title="Klonen"
                      onClick={() => { setCloningExt(ext); setShowCloneModal(true); }}>
                      <i className="bi bi-copy"></i>
                    </Button>
                    <Button variant="outline-primary" size="sm" className="me-1" onClick={() => { setEditingExt(ext); setShowModal(true); }}>
                      <i className="bi bi-pencil"></i>
                    </Button>
                    <Button variant="outline-danger" size="sm" onClick={() => handleDelete(ext.id)}>
                      <i className="bi bi-trash"></i>
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      <ExtensionModal
        show={showModal}
        onHide={() => setShowModal(false)}
        extension={editingExt}
        onSave={handleSave}
        allExtensions={extensions}
        tenantDomain={editingExt?.tenant_domain || selectedTenant?.domain}
        tenantId={editingExt?.tenant_id || selectedTenant?.id}
      />

      <CloneModal
        show={showCloneModal}
        onHide={() => setShowCloneModal(false)}
        extension={cloningExt}
        onClone={handleClone}
        existingExtensions={extensions.map(e => e.extension)}
      />

      {/* QR Code Modal */}
      <Modal show={showQrModal} onHide={() => setShowQrModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-qr-code me-2"></i>
            Softphone QR-Code
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {qrLoading ? (
            <div className="py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Laden...</span>
              </div>
              <p className="mt-3 text-muted">QR-Code wird generiert...</p>
            </div>
          ) : (
            <>
              <p className="mb-3">
                Scannen Sie diesen QR-Code mit der <strong>it64PbxPhone</strong> App,
                um die Nebenstelle <strong>{qrExt?.extension}</strong> automatisch zu konfigurieren.
              </p>
              <div className="bg-white p-3 d-inline-block rounded border">
                <img src={qrCodeUrl} alt="QR-Code" style={{ width: 200, height: 200 }} />
              </div>
              <div className="alert alert-warning mt-3 mb-0 text-start">
                <i className="bi bi-exclamation-triangle me-2"></i>
                <strong>Sicherheitshinweis:</strong> Dieser QR-Code ist <strong>10 Minuten</strong> gültig
                und kann nur <strong>einmal</strong> verwendet werden.
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => {
            handleShowQrCode(qrExt!)
          }}>
            <i className="bi bi-arrow-clockwise me-1"></i> Neuer Code
          </Button>
          <Button variant="secondary" onClick={() => setShowQrModal(false)}>
            Schliessen
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

interface ExtensionModalProps {
  show: boolean
  onHide: () => void
  extension: Extension | null
  onSave: (data: Record<string, unknown>) => void
  allExtensions: Extension[]
  tenantDomain?: string
  tenantId?: string
}

function ExtensionModal({ show, onHide, extension, onSave, allExtensions, tenantDomain, tenantId }: ExtensionModalProps) {
  const [formData, setFormData] = useState<typeof defaultFormData>(defaultFormData)
  const [cidRules, setCidRules] = useState<Array<{ pattern: string; cid: string }>>([])
  const [trunks, setTrunks] = useState<Trunk[]>([])
  const [selectedTrunkIds, setSelectedTrunkIds] = useState<string[]>([])
  const [vmEnabledExts, setVmEnabledExts] = useState<Array<{ id: string; extension: string; name: string }>>([])
  const [cfSyncing, setCfSyncing] = useState(false)
  const [cfOutOfSync, setCfOutOfSync] = useState(false)

  // Load trunks and voicemail-enabled extensions when modal opens
  useEffect(() => {
    if (show) {
      trunksApi.list().then(response => {
        setTrunks(response.data.items || [])
      }).catch(() => {})
      // Nur Mailboxen des eigenen Mandanten anbieten - MWI wird immer mit der
      // eigenen Domain gerendert, fremde Nebenstellen ergaeben eine tote Mailbox
      extensionsApi.voicemailEnabled(tenantId).then(response => {
        setVmEnabledExts(response.data || [])
      }).catch(() => {})
    }
  }, [show, tenantId])

  // Check live CF status when editing existing extension
  useEffect(() => {
    if (show && extension) {
      setCfOutOfSync(false)
      extensionsApi.getCfStatus(extension.id).then(response => {
        const d = response.data
        // Check if AstDB state differs from GUI DB state
        const astCf = d.cf || null
        const astCfb = d.cfb || null
        const astCfna = d.cfna || null
        const guiCf = d.db_forward_unconditional_enabled ? d.db_forward_unconditional : null
        const guiCfb = d.db_forward_busy_enabled ? d.db_forward_busy : null
        const guiCfna = d.db_forward_noanswer_enabled ? d.db_forward_noanswer : null
        if (astCf !== guiCf || astCfb !== guiCfb || astCfna !== guiCfna) {
          setCfOutOfSync(true)
        }
      }).catch(() => {
        // Ignore
      })
    }
  }, [show, extension])

  const handleSyncCfFromPhone = async () => {
    if (!extension) return
    setCfSyncing(true)
    try {
      const response = await extensionsApi.syncCfFromPhone(extension.id)
      const d = response.data
      setFormData(prev => ({
        ...prev,
        forward_unconditional: d.forward_unconditional || '',
        forward_unconditional_enabled: d.forward_unconditional_enabled,
        forward_busy: d.forward_busy || '',
        forward_busy_enabled: d.forward_busy_enabled,
        forward_noanswer: d.forward_noanswer || '',
        forward_noanswer_enabled: d.forward_noanswer_enabled,
      }))
      setCfOutOfSync(false)
      toast.success('Rufumleitung von Asterisk synchronisiert')
    } catch {
      toast.error('Fehler beim Synchronisieren')
    } finally {
      setCfSyncing(false)
    }
  }

  // Reset form when modal opens or extension changes
  useEffect(() => {
    if (!show) return // Don't reset when modal is closing

    if (extension) {
      setFormData({
        ...defaultFormData,
        ...extension,
        password: extension.password || '',
        caller_id: extension.caller_id || '',
        voicemail_pin: extension.voicemail_pin || '',
        mwi_mailbox: extension.mwi_mailbox || '',
        voicemail_mailboxes: extension.voicemail_mailboxes || [],
        forward_unconditional: extension.forward_unconditional || '',
        forward_busy: extension.forward_busy || '',
        forward_busy_internal: extension.forward_busy_internal || '',
        forward_busy_external: extension.forward_busy_external || '',
        forward_noanswer: extension.forward_noanswer || '',
        forward_noanswer_internal: extension.forward_noanswer_internal || '',
        forward_noanswer_external: extension.forward_noanswer_external || '',
        forward_unavailable: extension.forward_unavailable || '',
        outgoing_cid_default: extension.outgoing_cid_default || '',
        voicemail_email: extension.voicemail_email || '',
        voicemail_trunk_ids: extension.voicemail_trunk_ids || '[]',
        notes: extension.notes || ''
      })
      // Parse trunk IDs
      try {
        setSelectedTrunkIds(JSON.parse(extension.voicemail_trunk_ids || '[]'))
      } catch {
        setSelectedTrunkIds([])
      }
      try {
        setCidRules(JSON.parse(extension.outgoing_cid_rules || '[]'))
      } catch {
        setCidRules([])
      }
    } else {
      // Reset to defaults for new extension
      setFormData({ ...defaultFormData })
      setCidRules([])
      setSelectedTrunkIds([])
    }
  }, [extension, show])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    // Capture a mailbox the user selected in the dropdown but didn't confirm with "+"
    const vmSelect = document.getElementById('vm-mailbox-select') as HTMLSelectElement | null
    const pendingMailbox = vmSelect?.value
    const voicemailMailboxes = [...(formData.voicemail_mailboxes || [])]
    if (pendingMailbox && !voicemailMailboxes.includes(pendingMailbox)) {
      voicemailMailboxes.push(pendingMailbox)
    }
    const data = {
      ...formData,
      voicemail_mailboxes: voicemailMailboxes,
      outgoing_cid_rules: JSON.stringify(cidRules),
      voicemail_trunk_ids: JSON.stringify(selectedTrunkIds)
    }
    onSave(data)
  }

  const toggleTrunkSelection = (trunkId: string) => {
    setSelectedTrunkIds(prev =>
      prev.includes(trunkId)
        ? prev.filter(id => id !== trunkId)
        : [...prev, trunkId]
    )
  }

  const addCidRule = () => {
    setCidRules([...cidRules, { pattern: '', cid: '' }])
  }

  const removeCidRule = (index: number) => {
    setCidRules(cidRules.filter((_, i) => i !== index))
  }

  const updateCidRule = (index: number, field: 'pattern' | 'cid', value: string) => {
    const updated = [...cidRules]
    updated[index][field] = value
    setCidRules(updated)
  }

  // Get other extensions for forwarding targets
  const forwardTargets = allExtensions.filter(e => e.extension !== formData.extension)

  return (
    <Modal show={show} onHide={onHide} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>{extension ? `Nebenstelle ${extension.extension} bearbeiten` : 'Neue Nebenstelle'}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Tabs defaultActiveKey="basis" className="mb-3">
            {/* Tab: Basis */}
            <Tab eventKey="basis" title="Basis">
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Nebenstelle *</Form.Label>
                    <Form.Control
                      type="text"
                      inputMode="numeric"
                      maxLength={10}
                      value={formData.extension}
                      onChange={(e) => setFormData({ ...formData, extension: e.target.value.replace(/\D/g, '') })}
                      required
                      disabled={!!extension}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Name *</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Caller-ID (intern)</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.caller_id || ''}
                      onChange={(e) => setFormData({ ...formData, caller_id: e.target.value })}
                      placeholder={formData.name || 'Name'}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>SIP Passwort</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.password || ''}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder="Automatisch generiert wenn leer"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Klingeldauer (Sekunden)</Form.Label>
                    <Form.Control
                      type="number"
                      min={0}
                      max={180}
                      value={formData.ring_timeout}
                      onChange={(e) => setFormData({ ...formData, ring_timeout: parseInt(e.target.value) || 0 })}
                    />
                    <Form.Text className="text-muted">
                      0 = Direkt zur Mailbox (Telefonbeantworter-Modus)
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              {extension && (
                <Card className={`mb-3 ${!formData.is_active ? 'border-danger' : ''}`}>
                  <Card.Body className="py-2">
                    <Form.Check
                      type="switch"
                      id="is_active_switch"
                      label={<strong>{formData.is_active ? 'Nebenstelle aktiv' : 'Nebenstelle deaktiviert'}</strong>}
                      checked={formData.is_active !== false}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    />
                    {!formData.is_active && (
                      <Form.Text className="text-danger">
                        <i className="bi bi-exclamation-triangle me-1"></i>
                        Deaktivierte Nebenstellen können sich nicht registrieren und keine Anrufe tätigen oder empfangen.
                      </Form.Text>
                    )}
                  </Card.Body>
                </Card>
              )}

              <Card className="mb-3">
                <Card.Body>
                  <Row className="mb-2">
                    <Col md={3}>
                      <Form.Check
                        type="switch"
                        label="Anklopfen"
                        checked={formData.call_waiting}
                        onChange={(e) => setFormData({ ...formData, call_waiting: e.target.checked })}
                      />
                      <Form.Text className="text-muted">
                        Zweiter Anruf klopft im laufenden Gespräch an, statt besetzt zu geben.
                        Gilt für die Windows- und die Android-App dieser Nebenstelle; Tischtelefone
                        haben eine eigene Einstellung im Provisioning-Profil.
                      </Form.Text>
                    </Col>
                    <Col md={3}>
                      <Form.Check
                        type="switch"
                        label="Anrufaufzeichnung"
                        checked={formData.call_recording}
                        onChange={(e) => setFormData({ ...formData, call_recording: e.target.checked })}
                      />
                      <Form.Text className="text-muted">
                        Nimmt automatisch auf (mit Ansage). Wirkt erst, wenn unter
                        «Gesprächsaufnahme» der Rechtshinweis bestätigt und ein
                        Speicherort getestet ist.
                      </Form.Text>
                    </Col>
                    <Col md={3}>
                      <Form.Check
                        type="switch"
                        label="BLF/Hints"
                        checked={formData.hint_enabled}
                        onChange={(e) => setFormData({ ...formData, hint_enabled: e.target.checked })}
                      />
                    </Col>
                    <Col md={3}>
                      <Form.Check
                        type="switch"
                        label="Nicht stören (DND)"
                        checked={formData.dnd_enabled}
                        onChange={(e) => setFormData({ ...formData, dnd_enabled: e.target.checked })}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <Form.Check
                        type="switch"
                        label="Im Telefonbuch anzeigen"
                        checked={formData.show_in_phonebook}
                        onChange={(e) => setFormData({ ...formData, show_in_phonebook: e.target.checked })}
                      />
                      <Form.Text className="text-muted">
                        Wenn deaktiviert, erscheint diese Nebenstelle nicht im LDAP-Telefonbuch
                      </Form.Text>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Sprache (Ansagen)</Form.Label>
                        <Form.Select
                          value={formData.language || 'en'}
                          onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                        >
                          <option value="de">Deutsch</option>
                          <option value="en">English</option>
                          <option value="fr">Français</option>
                          <option value="it">Italiano</option>
                        </Form.Select>
                        <Form.Text className="text-muted">
                          Sprache für Voicemail, Zeitansage und Feature-Codes
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                  {/* Multi-Device / SIP Settings */}
                  <Card className="mb-3 border-info">
                    <Card.Header className="bg-info text-white">
                      <i className="bi bi-phone me-2"></i>
                      <strong>Multi-Device / SIP</strong>
                    </Card.Header>
                    <Card.Body>
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Max. Registrierungen</Form.Label>
                            <Form.Control
                              type="number"
                              min={1}
                              max={10}
                              value={formData.max_contacts || 1}
                              onChange={(e) => setFormData({ ...formData, max_contacts: parseInt(e.target.value) || 1 })}
                            />
                            <Form.Text className="text-muted">
                              Anzahl Geräte, die sich gleichzeitig mit diesem SIP-Konto registrieren können.
                              Bei 1 = nur ein Gerät, bei höheren Werten klingeln alle registrierten Geräte gleichzeitig.
                            </Form.Text>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Besetzt ab # Gesprächen</Form.Label>
                            <Form.Control
                              type="number"
                              min={0}
                              max={10}
                              value={formData.device_state_busy_at ?? 1}
                              onChange={(e) => setFormData({ ...formData, device_state_busy_at: parseInt(e.target.value) || 0 })}
                            />
                            <Form.Text className="text-muted">
                              Ab wie vielen aktiven Gesprächen die Nebenstelle als "besetzt" gemeldet wird.
                              0 = nie besetzt (immer anklopfen), 1 = nach 1 Gespräch besetzt.
                            </Form.Text>
                          </Form.Group>
                        </Col>
                      </Row>
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Call Group</Form.Label>
                            <Form.Control
                              type="number"
                              min={1}
                              max={63}
                              value={formData.call_group ?? 1}
                              onChange={(e) => setFormData({ ...formData, call_group: parseInt(e.target.value) || 1 })}
                            />
                            <Form.Text className="text-muted">
                              Anrufgruppe dieser Nebenstelle (1-63). Andere können Anrufe dieser Gruppe heranholen.
                            </Form.Text>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Pickup Group</Form.Label>
                            <Form.Control
                              type="number"
                              min={1}
                              max={63}
                              value={formData.pickup_group ?? 1}
                              onChange={(e) => setFormData({ ...formData, pickup_group: parseInt(e.target.value) || 1 })}
                            />
                            <Form.Text className="text-muted">
                              Von welcher Gruppe kann diese Nebenstelle Anrufe heranholen (1-63).
                            </Form.Text>
                          </Form.Group>
                        </Col>
                      </Row>
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label><i className="bi bi-shield-lock me-1"></i>Sprachverschlüsselung</Form.Label>
                            <Form.Select
                              value={formData.media_encryption || 'off'}
                              onChange={(e) => setFormData({ ...formData, media_encryption: e.target.value as Extension['media_encryption'] })}
                            >
                              <option value="off">Aus (unverschlüsselt)</option>
                              <option value="optional">Bevorzugt (SRTP wenn möglich)</option>
                              <option value="mandatory">Erzwungen (nur SRTP)</option>
                            </Form.Select>
                            <Form.Text className="text-muted">
                              Verschlüsselt Sprache (SRTP) und Anmeldung (TLS) zwischen Gerät und Anlage.
                              Gespräche nach aussen bleiben ab der Anlage unverschlüsselt.
                            </Form.Text>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          {formData.media_encryption === 'optional' && (
                            <div className="alert alert-info small mb-3">
                              Apps mit Verschlüsselungs-Unterstützung melden sich per TLS an und telefonieren
                              verschlüsselt. Ältere Apps und Tischtelefone telefonieren unverschlüsselt weiter.
                            </div>
                          )}
                          {formData.media_encryption === 'mandatory' && (
                            <div className="alert alert-warning small mb-3">
                              <i className="bi bi-exclamation-triangle me-1"></i>
                              Nur Geräte mit SRTP können telefonieren. Ältere App-Versionen und Tischtelefone
                              ohne SRTP-Einstellung scheitern bei jedem Gespräch.
                            </div>
                          )}
                        </Col>
                      </Row>
                      {(formData.max_contacts || 1) > 1 && (
                        <div className="alert alert-info mb-0">
                          <i className="bi bi-info-circle me-2"></i>
                          <strong>Multi-Device aktiv:</strong> Eingehende Anrufe klingeln auf allen {formData.max_contacts} registrierten Geräten gleichzeitig.
                          {(formData.device_state_busy_at || 1) === 1 && (
                            <span> Sobald ein Gerät ein Gespräch führt, wird die Nebenstelle als besetzt gemeldet.</span>
                          )}
                          {(formData.device_state_busy_at || 1) > 1 && (
                            <span> Die Nebenstelle wird erst nach {formData.device_state_busy_at} gleichzeitigen Gesprächen als besetzt gemeldet.</span>
                          )}
                          {(formData.device_state_busy_at || 0) === 0 && (
                            <span> Die Nebenstelle wird nie als besetzt gemeldet (Anklopfen für alle).</span>
                          )}
                        </div>
                      )}
                    </Card.Body>
                  </Card>

                  <Row className="mt-3">
                    <Col md={12}>
                      <Form.Group>
                        <Form.Label>Audio-Codecs <small className="text-muted">(Reihenfolge = Priorität)</small></Form.Label>
                        <div className="border rounded p-2">
                          {(() => {
                            const availableCodecs = [
                              { id: 'g722', name: 'G.722', description: 'HD-Voice (16kHz, 64kbps)', quality: 'Empfohlen' },
                                                            { id: 'ulaw', name: 'G.711 µ-law', description: 'Standard (8kHz, 64kbps)', quality: 'Kompatibel' },
                              { id: 'alaw', name: 'G.711 A-law', description: 'Europa (8kHz, 64kbps)', quality: 'Kompatibel' },
                              { id: 'g729', name: 'G.729', description: 'Komprimiert (8kHz, 8kbps)', quality: 'Niedrige Bandbreite' }
                            ]
                            const selectedCodecs = (formData.codecs || 'g722,ulaw,alaw').split(',').filter(c => c)

                            const moveCodec = (index: number, direction: 'up' | 'down') => {
                              const newCodecs = [...selectedCodecs]
                              const newIndex = direction === 'up' ? index - 1 : index + 1
                              if (newIndex >= 0 && newIndex < newCodecs.length) {
                                [newCodecs[index], newCodecs[newIndex]] = [newCodecs[newIndex], newCodecs[index]]
                                setFormData({ ...formData, codecs: newCodecs.join(',') })
                              }
                            }

                            const toggleCodec = (codecId: string) => {
                              let newCodecs = [...selectedCodecs]
                              if (newCodecs.includes(codecId)) {
                                newCodecs = newCodecs.filter(c => c !== codecId)
                              } else {
                                newCodecs.push(codecId)
                              }
                              setFormData({ ...formData, codecs: newCodecs.join(',') })
                            }

                            return (
                              <>
                                <div className="mb-2">
                                  <small className="text-muted">Ausgewählt (in Prioritätsreihenfolge):</small>
                                </div>
                                {selectedCodecs.map((codecId, index) => {
                                  const codec = availableCodecs.find(c => c.id === codecId)
                                  if (!codec) return null
                                  return (
                                    <div key={codecId} className="d-flex align-items-center mb-1 p-1 bg-light rounded">
                                      <Badge bg="primary" className="me-2">{index + 1}</Badge>
                                      <div className="flex-grow-1">
                                        <strong>{codec.name}</strong>
                                        <small className="text-muted ms-2">{codec.description}</small>
                                      </div>
                                      <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        className="me-1 py-0 px-1"
                                        onClick={() => moveCodec(index, 'up')}
                                        disabled={index === 0}
                                      >
                                        <i className="bi bi-arrow-up"></i>
                                      </Button>
                                      <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        className="me-1 py-0 px-1"
                                        onClick={() => moveCodec(index, 'down')}
                                        disabled={index === selectedCodecs.length - 1}
                                      >
                                        <i className="bi bi-arrow-down"></i>
                                      </Button>
                                      <Button
                                        variant="outline-danger"
                                        size="sm"
                                        className="py-0 px-1"
                                        onClick={() => toggleCodec(codecId)}
                                      >
                                        <i className="bi bi-x"></i>
                                      </Button>
                                    </div>
                                  )
                                })}
                                <hr className="my-2" />
                                <small className="text-muted">Verfügbar:</small>
                                <div className="d-flex flex-wrap gap-1 mt-1">
                                  {availableCodecs.filter(c => !selectedCodecs.includes(c.id)).map(codec => (
                                    <Button
                                      key={codec.id}
                                      variant="outline-primary"
                                      size="sm"
                                      onClick={() => toggleCodec(codec.id)}
                                    >
                                      <i className="bi bi-plus me-1"></i>{codec.name}
                                    </Button>
                                  ))}
                                  {availableCodecs.filter(c => !selectedCodecs.includes(c.id)).length === 0 && (
                                    <span className="text-muted">Alle Codecs ausgewählt</span>
                                  )}
                                </div>
                              </>
                            )
                          })()}
                        </div>
                        <Form.Text className="text-muted">
                          G.722 bietet die beste Balance aus Qualität und Kompatibilität
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              <Form.Group className="mb-3">
                <Form.Label>Notizen</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={formData.notes || ''}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                />
              </Form.Group>
            </Tab>

            {/* Tab: Rufumleitungen */}
            <Tab eventKey="forwarding" title={<>Rufumleitungen{cfOutOfSync && <i className="bi bi-exclamation-circle text-warning ms-1"></i>}</>}>
              {cfOutOfSync && extension && (
                <div className="alert alert-warning d-flex align-items-center py-2 mb-3">
                  <i className="bi bi-telephone-fill me-2"></i>
                  <span className="flex-grow-1 small">
                    Die Rufumleitung wurde am Telefon geändert und stimmt nicht mit dem GUI überein.
                  </span>
                  <Button
                    variant="warning"
                    size="sm"
                    onClick={handleSyncCfFromPhone}
                    disabled={cfSyncing}
                  >
                    {cfSyncing ? <><i className="bi bi-arrow-repeat spin me-1"></i>Sync...</> : <><i className="bi bi-arrow-repeat me-1"></i>Von Telefon übernehmen</>}
                  </Button>
                </div>
              )}
              {/* Unconditional */}
              <Card className="mb-3">
                <Card.Header>
                  <Form.Check
                    type="switch"
                    label={<strong>Immer umleiten</strong>}
                    checked={formData.forward_unconditional_enabled}
                    onChange={(e) => setFormData({ ...formData, forward_unconditional_enabled: e.target.checked })}
                  />
                </Card.Header>
                {formData.forward_unconditional_enabled && (
                  <Card.Body>
                    <Form.Group>
                      <Form.Label>Ziel</Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.forward_unconditional || ''}
                        onChange={(e) => setFormData({ ...formData, forward_unconditional: e.target.value })}
                        placeholder="Nebenstelle oder externe Nummer"
                        list="ext-list"
                      />
                    </Form.Group>
                  </Card.Body>
                )}
              </Card>

              {/* Busy */}
              <Card className="mb-3">
                <Card.Header>
                  <Form.Check
                    type="switch"
                    label={<strong>Bei Besetzt umleiten</strong>}
                    checked={formData.forward_busy_enabled}
                    onChange={(e) => setFormData({ ...formData, forward_busy_enabled: e.target.checked })}
                  />
                </Card.Header>
                {formData.forward_busy_enabled && (
                  <Card.Body>
                    <Row>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>Standard-Ziel</Form.Label>
                          <Form.Control
                            type="text"
                            value={formData.forward_busy || ''}
                            onChange={(e) => setFormData({ ...formData, forward_busy: e.target.value })}
                            placeholder="Für alle Anrufe"
                            list="ext-list"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>Interne Anrufe</Form.Label>
                          <Form.Control
                            type="text"
                            value={formData.forward_busy_internal || ''}
                            onChange={(e) => setFormData({ ...formData, forward_busy_internal: e.target.value })}
                            placeholder="Leer = Standard"
                            list="ext-list"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>Externe Anrufe</Form.Label>
                          <Form.Control
                            type="text"
                            value={formData.forward_busy_external || ''}
                            onChange={(e) => setFormData({ ...formData, forward_busy_external: e.target.value })}
                            placeholder="Leer = Standard"
                            list="ext-list"
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </Card.Body>
                )}
              </Card>

              {/* No Answer */}
              <Card className="mb-3">
                <Card.Header>
                  <Form.Check
                    type="switch"
                    label={<strong>Bei Nichtannahme umleiten</strong>}
                    checked={formData.forward_noanswer_enabled}
                    onChange={(e) => setFormData({ ...formData, forward_noanswer_enabled: e.target.checked })}
                  />
                </Card.Header>
                {formData.forward_noanswer_enabled && (
                  <Card.Body>
                    <Row>
                      <Col md={3}>
                        <Form.Group className="mb-3">
                          <Form.Label>Nach Sekunden</Form.Label>
                          <Form.Control
                            type="number"
                            min={5}
                            max={120}
                            value={formData.forward_noanswer_timeout}
                            onChange={(e) => setFormData({ ...formData, forward_noanswer_timeout: parseInt(e.target.value) || 20 })}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group className="mb-3">
                          <Form.Label>Standard-Ziel</Form.Label>
                          <Form.Control
                            type="text"
                            value={formData.forward_noanswer || ''}
                            onChange={(e) => setFormData({ ...formData, forward_noanswer: e.target.value })}
                            list="ext-list"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group className="mb-3">
                          <Form.Label>Interne Anrufe</Form.Label>
                          <Form.Control
                            type="text"
                            value={formData.forward_noanswer_internal || ''}
                            onChange={(e) => setFormData({ ...formData, forward_noanswer_internal: e.target.value })}
                            placeholder="Leer = Standard"
                            list="ext-list"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group className="mb-3">
                          <Form.Label>Externe Anrufe</Form.Label>
                          <Form.Control
                            type="text"
                            value={formData.forward_noanswer_external || ''}
                            onChange={(e) => setFormData({ ...formData, forward_noanswer_external: e.target.value })}
                            placeholder="Leer = Standard"
                            list="ext-list"
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </Card.Body>
                )}
              </Card>

              {/* Unavailable */}
              <Card className="mb-3">
                <Card.Header>
                  <Form.Check
                    type="switch"
                    label={<strong>Bei Nicht-Erreichbarkeit umleiten</strong>}
                    checked={formData.forward_unavailable_enabled}
                    onChange={(e) => setFormData({ ...formData, forward_unavailable_enabled: e.target.checked })}
                  />
                </Card.Header>
                {formData.forward_unavailable_enabled && (
                  <Card.Body>
                    <Form.Group>
                      <Form.Label>Ziel (wenn Telefon offline)</Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.forward_unavailable || ''}
                        onChange={(e) => setFormData({ ...formData, forward_unavailable: e.target.value })}
                        placeholder="Nebenstelle oder externe Nummer (z.B. 0791234567)"
                        list="ext-list"
                      />
                      <Form.Text className="text-muted">
                        Greift wenn das Telefon nicht registriert ist oder die Verbindung scheitert (CHANUNAVAIL/CONGESTION).
                      </Form.Text>
                    </Form.Group>
                  </Card.Body>
                )}
              </Card>

              <datalist id="ext-list">
                {forwardTargets.map(e => (
                  <option key={e.id} value={e.extension}>{e.name}</option>
                ))}
              </datalist>
            </Tab>

            {/* Tab: Verhalten */}
            <Tab eventKey="behavior" title="Verhalten">
              <div className="alert alert-secondary mb-3">
                <i className="bi bi-info-circle me-2"></i>
                <strong>Hinweis:</strong> Diese Einstellungen gelten als <strong>Standard-Verhalten</strong>, wenn im Tab "Rufumleitungen" keine spezifische Weiterleitung aktiviert ist.
              </div>

              {/* Bei Besetzt */}
              <Card className="mb-3">
                <Card.Header>
                  <strong><i className="bi bi-telephone-x me-2"></i>Bei Besetzt</strong>
                  {formData.forward_busy_enabled && (
                    <Badge bg="warning" className="ms-2">Weiterleitung aktiv → Tab "Rufumleitungen"</Badge>
                  )}
                </Card.Header>
                <Card.Body>
                  {formData.forward_busy_enabled ? (
                    <div className="alert alert-warning mb-0">
                      <i className="bi bi-arrow-right-circle me-2"></i>
                      Weiterleitung bei Besetzt ist aktiviert zu: <strong>{formData.forward_busy || '(nicht gesetzt)'}</strong>
                      <br />
                      <small>Diese Einstellung überschreibt das Standard-Verhalten. Deaktivieren Sie die Weiterleitung im Tab "Rufumleitungen", um das Standard-Verhalten zu verwenden.</small>
                    </div>
                  ) : (
                    <Row>
                      <Col md={6}>
                        <Form.Label className="small fw-bold">Interne Anrufer:</Form.Label>
                        <Form.Check
                          type="radio"
                          name="busy_internal"
                          label="Besetztton senden"
                          checked={formData.busy_signal_internal}
                          onChange={() => setFormData({ ...formData, busy_signal_internal: true })}
                        />
                        <Form.Check
                          type="radio"
                          name="busy_internal"
                          label="Zur Voicemail"
                          checked={!formData.busy_signal_internal}
                          onChange={() => setFormData({ ...formData, busy_signal_internal: false })}
                          disabled={!formData.voicemail_enabled}
                        />
                        {!formData.voicemail_enabled && (
                          <Form.Text className="text-muted d-block">Voicemail nicht aktiviert</Form.Text>
                        )}
                      </Col>
                      <Col md={6}>
                        <Form.Label className="small fw-bold">Externe Anrufer:</Form.Label>
                        <Form.Check
                          type="radio"
                          name="busy_external"
                          label="Besetztton senden"
                          checked={formData.busy_signal_external}
                          onChange={() => setFormData({ ...formData, busy_signal_external: true })}
                        />
                        <Form.Check
                          type="radio"
                          name="busy_external"
                          label="Zur Voicemail"
                          checked={!formData.busy_signal_external}
                          onChange={() => setFormData({ ...formData, busy_signal_external: false })}
                          disabled={!formData.voicemail_enabled}
                        />
                        {!formData.voicemail_enabled && (
                          <Form.Text className="text-muted d-block">Voicemail nicht aktiviert</Form.Text>
                        )}
                      </Col>
                    </Row>
                  )}
                </Card.Body>
              </Card>

              {/* Bei Nichtannahme */}
              <Card className="mb-3">
                <Card.Header>
                  <strong><i className="bi bi-clock me-2"></i>Bei Nichtannahme</strong>
                  {formData.forward_noanswer_enabled && (
                    <Badge bg="warning" className="ms-2">Weiterleitung aktiv → Tab "Rufumleitungen"</Badge>
                  )}
                </Card.Header>
                <Card.Body>
                  {formData.forward_noanswer_enabled ? (
                    <div className="alert alert-warning mb-0">
                      <i className="bi bi-arrow-right-circle me-2"></i>
                      Weiterleitung bei Nichtannahme ist aktiviert zu: <strong>{formData.forward_noanswer || '(nicht gesetzt)'}</strong>
                      <br />
                      <small>Diese Einstellung überschreibt das Standard-Verhalten. Deaktivieren Sie die Weiterleitung im Tab "Rufumleitungen", um das Standard-Verhalten zu verwenden.</small>
                    </div>
                  ) : (
                    <>
                      <Row className="mb-3">
                        <Col md={4}>
                          <Form.Label className="small fw-bold">Nach Sekunden (Klingeldauer):</Form.Label>
                          <Form.Control
                            type="number"
                            min={0}
                            max={120}
                            value={formData.ring_timeout}
                            onChange={(e) => setFormData({ ...formData, ring_timeout: parseInt(e.target.value) || 0 })}
                            size="sm"
                          />
                          <Form.Text className="text-muted">0 = direkt zur Aktion (Telefonbeantworter-Modus)</Form.Text>
                        </Col>
                        <Col md={8}>
                          <Form.Label className="small fw-bold">Aktion nach Ablauf der Klingeldauer:</Form.Label>
                          <Form.Check
                            type="radio"
                            name="noanswer_action"
                            label="Zur Voicemail"
                            checked={formData.voicemail_unavail_enabled}
                            onChange={() => setFormData({ ...formData, voicemail_unavail_enabled: true })}
                            disabled={!formData.voicemail_enabled}
                          />
                          <Form.Check
                            type="radio"
                            name="noanswer_action"
                            label="Anruf beenden (kein Voicemail)"
                            checked={!formData.voicemail_unavail_enabled}
                            onChange={() => setFormData({ ...formData, voicemail_unavail_enabled: false })}
                          />
                          {!formData.voicemail_enabled && (
                            <Form.Text className="text-muted d-block mt-1">
                              <i className="bi bi-exclamation-triangle me-1"></i>
                              Voicemail nicht aktiviert - aktivieren Sie Voicemail im Tab "Voicemail"
                            </Form.Text>
                          )}
                        </Col>
                      </Row>
                    </>
                  )}
                </Card.Body>
              </Card>

              {/* Bei Nicht-Erreichbarkeit */}
              <Card className="mb-3">
                <Card.Header>
                  <strong><i className="bi bi-wifi-off me-2"></i>Bei Nicht-Erreichbarkeit (Telefon offline)</strong>
                  {formData.forward_unavailable_enabled && (
                    <Badge bg="warning" className="ms-2">Weiterleitung aktiv → Tab "Rufumleitungen"</Badge>
                  )}
                </Card.Header>
                <Card.Body>
                  {formData.forward_unavailable_enabled ? (
                    <div className="alert alert-warning mb-0">
                      <i className="bi bi-arrow-right-circle me-2"></i>
                      Weiterleitung bei Nicht-Erreichbarkeit ist aktiviert zu: <strong>{formData.forward_unavailable || '(nicht gesetzt)'}</strong>
                      <br />
                      <small>Diese Einstellung überschreibt das Standard-Verhalten. Deaktivieren Sie die Weiterleitung im Tab "Rufumleitungen", um das Standard-Verhalten zu verwenden.</small>
                    </div>
                  ) : (
                    <>
                      <Form.Label className="small fw-bold">Aktion wenn Telefon nicht registriert:</Form.Label>
                      {formData.voicemail_enabled ? (
                        <div className="alert alert-success mb-0">
                          <i className="bi bi-voicemail me-2"></i>
                          Anrufe werden an <strong>Voicemail</strong> weitergeleitet, da Voicemail aktiviert ist.
                        </div>
                      ) : (
                        <div className="alert alert-danger mb-0">
                          <i className="bi bi-x-circle me-2"></i>
                          Anrufe werden <strong>abgewiesen</strong>, da Voicemail nicht aktiviert ist.
                          <br />
                          <small>Aktivieren Sie Voicemail im Tab "Voicemail" oder richten Sie eine Weiterleitung im Tab "Rufumleitungen" ein.</small>
                        </div>
                      )}
                    </>
                  )}
                </Card.Body>
              </Card>

              {/* Anrufer-Screening */}
              <Card>
                <Card.Header><strong><i className="bi bi-person-badge me-2"></i>Anrufer-Screening</strong></Card.Header>
                <Card.Body>
                  <Form.Check
                    type="switch"
                    label="Anrufer-Namen vor Verbindung ansagen"
                    checked={formData.call_screening}
                    onChange={(e) => setFormData({ ...formData, call_screening: e.target.checked })}
                  />
                  <small className="text-muted">Der Anrufer wird aufgefordert seinen Namen zu sagen, bevor der Anruf durchgestellt wird.</small>
                </Card.Body>
              </Card>
            </Tab>

            {/* Tab: Ausgehende Anrufe */}
            <Tab eventKey="outgoing" title="Ausgehend">
              <Card className="mb-3">
                <Card.Header><strong>Ausgehende Rufnummer (Caller-ID)</strong></Card.Header>
                <Card.Body>
                  <Form.Group className="mb-3">
                    <Form.Label>Standard Caller-ID für ausgehende Anrufe</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.outgoing_cid_default || ''}
                      onChange={(e) => setFormData({ ...formData, outgoing_cid_default: e.target.value })}
                      placeholder="z.B. +41441234567"
                    />
                    <Form.Text className="text-muted">
                      Wenn leer, wird die Trunk-Standard-Nummer verwendet.
                    </Form.Text>
                  </Form.Group>
                </Card.Body>
              </Card>

              <Card>
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <strong>Zielabhängige Caller-ID Regeln</strong>
                  <Button variant="outline-primary" size="sm" onClick={addCidRule}>
                    <i className="bi bi-plus-lg me-1"></i> Regel hinzufügen
                  </Button>
                </Card.Header>
                <Card.Body>
                  {cidRules.length === 0 ? (
                    <p className="text-muted mb-0">Keine Regeln definiert. Es wird immer die Standard-Caller-ID verwendet.</p>
                  ) : (
                    <>
                      <p className="text-muted small">Für Anrufe zu bestimmten Nummern eine andere Caller-ID verwenden:</p>
                      {cidRules.map((rule, index) => (
                        <Row key={index} className="mb-2 align-items-center">
                          <Col md={5}>
                            <Form.Control
                              type="text"
                              placeholder="Zielmuster (z.B. 0041* oder 0049*)"
                              value={rule.pattern}
                              onChange={(e) => updateCidRule(index, 'pattern', e.target.value)}
                            />
                          </Col>
                          <Col md={1} className="text-center">
                            <i className="bi bi-arrow-right"></i>
                          </Col>
                          <Col md={5}>
                            <Form.Control
                              type="text"
                              placeholder="Caller-ID (z.B. +41...)"
                              value={rule.cid}
                              onChange={(e) => updateCidRule(index, 'cid', e.target.value)}
                            />
                          </Col>
                          <Col md={1}>
                            <Button variant="outline-danger" size="sm" onClick={() => removeCidRule(index)}>
                              <i className="bi bi-trash"></i>
                            </Button>
                          </Col>
                        </Row>
                      ))}
                    </>
                  )}
                </Card.Body>
              </Card>
            </Tab>

            {/* Tab: Voicemail */}
            <Tab eventKey="voicemail" title="Voicemail">
              <Card className="mb-3">
                <Card.Header><strong>E-Mail-Adresse</strong></Card.Header>
                <Card.Body>
                  <Form.Group className="mb-0">
                    <Form.Control
                      type="email"
                      value={formData.voicemail_email || ''}
                      onChange={(e) => setFormData({ ...formData, voicemail_email: e.target.value })}
                      placeholder="user@example.com"
                    />
                    <Form.Text className="text-muted">
                      Wird für Voicemail-Benachrichtigungen und Softphone-Provisioning verwendet
                    </Form.Text>
                  </Form.Group>
                </Card.Body>
              </Card>
              <Card className="mb-3">
                <Card.Header>
                  <Form.Check
                    type="switch"
                    label={<strong>Voicemail aktivieren</strong>}
                    checked={formData.voicemail_enabled}
                    onChange={(e) => setFormData({ ...formData, voicemail_enabled: e.target.checked })}
                  />
                </Card.Header>
                {formData.voicemail_enabled && (
                  <Card.Body>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Voicemail PIN</Form.Label>
                          <Form.Control
                            type="text"
                            value={formData.voicemail_pin || ''}
                            onChange={(e) => setFormData({ ...formData, voicemail_pin: e.target.value })}
                            placeholder="Automatisch generiert"
                            maxLength={10}
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Card className="mb-3 border-info">
                      <Card.Body className="py-2">
                        <Form.Check
                          type="switch"
                          label={<strong>Mailbox-Ansage abspielen</strong>}
                          checked={formData.voicemail_greeting_enabled}
                          onChange={(e) => setFormData({ ...formData, voicemail_greeting_enabled: e.target.checked })}
                        />
                        <Form.Text className="text-muted">
                          Wenn deaktiviert, wird keine Ansage abgespielt und der Anrufer kann sofort eine Nachricht hinterlassen.
                        </Form.Text>
                      </Card.Body>
                    </Card>

                    <Card className="mb-3 border-warning">
                      <Card.Body className="py-2">
                        <Form.Check
                          type="switch"
                          label={<strong>Nur für bestimmte Trunks aktivieren</strong>}
                          checked={formData.voicemail_trunk_only}
                          onChange={(e) => {
                            setFormData({ ...formData, voicemail_trunk_only: e.target.checked })
                            if (!e.target.checked) setSelectedTrunkIds([])
                          }}
                        />
                        <Form.Text className="text-muted">
                          Voicemail nur für direkte Anrufe von ausgewählten Trunks aktivieren (z.B. externe Anrufe).
                        </Form.Text>
                        {formData.voicemail_trunk_only && (
                          <div className="mt-2">
                            {trunks.length === 0 ? (
                              <small className="text-muted">Keine Trunks verfügbar</small>
                            ) : (
                              trunks.map(trunk => (
                                <Form.Check
                                  key={trunk.id}
                                  type="checkbox"
                                  label={trunk.name}
                                  checked={selectedTrunkIds.includes(trunk.id)}
                                  onChange={() => toggleTrunkSelection(trunk.id)}
                                  className="ms-3"
                                />
                              ))
                            )}
                          </div>
                        )}
                      </Card.Body>
                    </Card>

                    <Row>
                      <Col md={6}>
                        <Form.Check
                          type="switch"
                          label="Voicemail bei Besetzt"
                          checked={formData.voicemail_busy_enabled}
                          onChange={(e) => setFormData({ ...formData, voicemail_busy_enabled: e.target.checked })}
                          className="mb-2"
                        />
                        <Form.Check
                          type="switch"
                          label="Voicemail bei Nichtannahme"
                          checked={formData.voicemail_unavail_enabled}
                          onChange={(e) => setFormData({ ...formData, voicemail_unavail_enabled: e.target.checked })}
                          className="mb-2"
                        />
                        <Form.Check
                          type="switch"
                          label="Anrufer-ID in Nachricht ansagen"
                          checked={formData.voicemail_announce_cid}
                          onChange={(e) => setFormData({ ...formData, voicemail_announce_cid: e.target.checked })}
                        />
                      </Col>
                      <Col md={6}>
                        <Form.Check
                          type="switch"
                          label="Audio-Datei an E-Mail anhängen"
                          checked={formData.voicemail_email_attach}
                          onChange={(e) => setFormData({ ...formData, voicemail_email_attach: e.target.checked })}
                          className="mb-2"
                        />
                        <Form.Check
                          type="switch"
                          label="Nach E-Mail-Versand löschen"
                          checked={formData.voicemail_delete_after_email}
                          onChange={(e) => setFormData({ ...formData, voicemail_delete_after_email: e.target.checked })}
                        />
                      </Col>
                    </Row>
                  </Card.Body>
                )}
              </Card>

              <Card className="mb-3">
                <Card.Header>
                  <strong><i className="bi bi-envelope me-2"></i>Mailboxen überwachen</strong>
                  <small className="text-muted ms-2">(MWI / LED)</small>
                </Card.Header>
                <Card.Body>
                  <Form.Group>
                    <div className="d-flex gap-2 mb-2">
                      <Form.Select
                        size="sm"
                        id="vm-mailbox-select"
                        defaultValue=""
                      >
                        <option value="">Mailbox hinzufügen...</option>
                        {vmEnabledExts
                          .filter(ve => ve.extension !== formData.extension && !(formData.voicemail_mailboxes || []).includes(ve.extension))
                          .map(ve => (
                            <option key={ve.id} value={ve.extension}>{ve.extension} - {ve.name}</option>
                          ))}
                      </Form.Select>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => {
                          const select = document.getElementById('vm-mailbox-select') as HTMLSelectElement
                          if (select?.value) {
                            setFormData({
                              ...formData,
                              voicemail_mailboxes: [...(formData.voicemail_mailboxes || []), select.value]
                            })
                            select.value = ''
                          }
                        }}
                      >
                        +
                      </Button>
                    </div>
                    {(formData.voicemail_mailboxes || []).length > 0 && (
                      <div className="border rounded p-2">
                        {(formData.voicemail_mailboxes || []).map((mb, idx) => {
                          const ext = vmEnabledExts.find(ve => ve.extension === mb)
                          return (
                            <div key={mb} className="d-flex align-items-center justify-content-between py-1 border-bottom">
                              <div className="d-flex align-items-center gap-2">
                                <div className="d-flex flex-column">
                                  <Button
                                    variant="link"
                                    size="sm"
                                    className="p-0 lh-1"
                                    disabled={idx === 0}
                                    onClick={() => {
                                      const arr = [...(formData.voicemail_mailboxes || [])]
                                      ;[arr[idx - 1], arr[idx]] = [arr[idx], arr[idx - 1]]
                                      setFormData({ ...formData, voicemail_mailboxes: arr })
                                    }}
                                  >&#9650;</Button>
                                  <Button
                                    variant="link"
                                    size="sm"
                                    className="p-0 lh-1"
                                    disabled={idx === (formData.voicemail_mailboxes || []).length - 1}
                                    onClick={() => {
                                      const arr = [...(formData.voicemail_mailboxes || [])]
                                      ;[arr[idx], arr[idx + 1]] = [arr[idx + 1], arr[idx]]
                                      setFormData({ ...formData, voicemail_mailboxes: arr })
                                    }}
                                  >&#9660;</Button>
                                </div>
                                <strong>{mb}</strong>
                                {ext && <span className="text-muted">({ext.name})</span>}
                              </div>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => {
                                  setFormData({
                                    ...formData,
                                    voicemail_mailboxes: (formData.voicemail_mailboxes || []).filter((_, i) => i !== idx)
                                  })
                                }}
                              >X</Button>
                            </div>
                          )
                        })}
                      </div>
                    )}
                    <Form.Text className="text-muted">
                      Zusätzliche Mailboxen überwachen (z.B. gemeinsame Mailbox 900). LED/MWI blinkt bei neuen Nachrichten.
                      {formData.voicemail_enabled && ' Die eigene Mailbox wird automatisch überwacht.'}
                    </Form.Text>
                  </Form.Group>
                </Card.Body>
              </Card>
            </Tab>

            {/* Tab: LDAP-Telefonbuch */}
            <Tab eventKey="ldap" title="LDAP">
              <Card className="mb-3">
                <Card.Header>
                  <strong><i className="bi bi-book me-2"></i>LDAP-Telefonbuch Zugangsdaten</strong>
                </Card.Header>
                <Card.Body>
                  <p className="text-muted mb-3">
                    Verwenden Sie diese Einstellungen, um das LDAP-Telefonbuch auf IP-Telefonen (z.B. Yealink) einzurichten.
                  </p>

                  <div className="alert alert-warning mb-3">
                    <i className="bi bi-shield-lock me-2"></i>
                    <strong>Authentifizierung erforderlich:</strong> Anonymer Zugriff ist deaktiviert. Verwenden Sie die SIP-Zugangsdaten dieser Nebenstelle.
                  </div>

                  <div className="alert alert-success mb-3">
                    <i className="bi bi-shield-check me-2"></i>
                    <strong>Zwei Verbindungsoptionen verfügbar:</strong>
                  </div>

                  <Row>
                    <Col md={6}>
                      <Card className="mb-3 border-success">
                        <Card.Header className="bg-success text-white">
                          <i className="bi bi-lock me-2"></i>LDAPS (empfohlen)
                        </Card.Header>
                        <Card.Body>
                          <Form.Group className="mb-2">
                            <Form.Label className="small text-muted mb-1">Server</Form.Label>
                            <Form.Control type="text" value={window.location.hostname} readOnly className="bg-light" size="sm" />
                          </Form.Group>
                          <Form.Group className="mb-2">
                            <Form.Label className="small text-muted mb-1">Port</Form.Label>
                            <Form.Control type="text" value="636" readOnly className="bg-light" size="sm" />
                          </Form.Group>
                          <Form.Group className="mb-0">
                            <Form.Label className="small text-muted mb-1">TLS-Modus (Yealink)</Form.Label>
                            <Form.Control type="text" value="2 (LDAPS/SSL)" readOnly className="bg-light" size="sm" />
                          </Form.Group>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={6}>
                      <Card className="mb-3">
                        <Card.Header className="bg-secondary text-white">
                          <i className="bi bi-unlock me-2"></i>LDAP + StartTLS
                        </Card.Header>
                        <Card.Body>
                          <Form.Group className="mb-2">
                            <Form.Label className="small text-muted mb-1">Server</Form.Label>
                            <Form.Control type="text" value={window.location.hostname} readOnly className="bg-light" size="sm" />
                          </Form.Group>
                          <Form.Group className="mb-2">
                            <Form.Label className="small text-muted mb-1">Port</Form.Label>
                            <Form.Control type="text" value="389" readOnly className="bg-light" size="sm" />
                          </Form.Group>
                          <Form.Group className="mb-0">
                            <Form.Label className="small text-muted mb-1">TLS-Modus (Yealink)</Form.Label>
                            <Form.Control type="text" value="1 (StartTLS)" readOnly className="bg-light" size="sm" />
                          </Form.Group>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label>Base DN</Form.Label>
                    <Form.Control
                      type="text"
                      value={tenantDomain ? `dc=${tenantDomain},dc=phonebook,dc=local` : 'dc=phonebook,dc=local'}
                      readOnly
                      className="bg-light font-monospace"
                    />
                    <Form.Text className="text-muted">
                      Mandantenspezifische Base DN für isolierte Telefonbuch-Suche
                    </Form.Text>
                  </Form.Group>

                  <Card className="mb-3 border-primary">
                    <Card.Header className="bg-primary text-white">
                      <i className="bi bi-key me-2"></i>
                      <strong>Authentifizierung für diese Nebenstelle</strong>
                    </Card.Header>
                    <Card.Body>
                      {(!formData.extension || !tenantDomain) ? (
                        <div className="alert alert-secondary mb-0">
                          <i className="bi bi-info-circle me-2"></i>
                          Speichern Sie die Nebenstelle, um die LDAP-Zugangsdaten anzuzeigen.
                        </div>
                      ) : (
                        <>
                          <Row>
                            <Col md={12}>
                              <Form.Group className="mb-3">
                                <Form.Label>Bind DN (Benutzername)</Form.Label>
                                <Form.Control
                                  type="text"
                                  value={`uid=${formData.extension},dc=${tenantDomain},dc=phonebook,dc=local`}
                                  readOnly
                                  className="bg-light font-monospace"
                                />
                                <Form.Text className="text-muted">
                                  Vollständiger LDAP Bind DN
                                </Form.Text>
                              </Form.Group>
                            </Col>
                          </Row>
                          <Row>
                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label>Passwort</Form.Label>
                                <Form.Control
                                  type="text"
                                  value={formData.password || '(SIP-Passwort)'}
                                  readOnly
                                  className="bg-light"
                                />
                              </Form.Group>
                            </Col>
                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label>LDAP TLS</Form.Label>
                                <Form.Control
                                  type="text"
                                  value="StartTLS (empfohlen)"
                                  readOnly
                                  className="bg-light"
                                />
                              </Form.Group>
                            </Col>
                          </Row>
                        </>
                      )}
                    </Card.Body>
                  </Card>

                  <hr />

                  <h6 className="mb-3"><i className="bi bi-phone me-2"></i>Yealink Telefon-Einstellungen</h6>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>LDAP Name Filter</Form.Label>
                        <Form.Control
                          type="text"
                          value="(|(cn=%)(sn=%))"
                          readOnly
                          className="bg-light font-monospace"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>LDAP Number Filter</Form.Label>
                        <Form.Control
                          type="text"
                          value="(|(telephoneNumber=%)(mobile=%))"
                          readOnly
                          className="bg-light font-monospace"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Name Attribute</Form.Label>
                        <Form.Control
                          type="text"
                          value="cn"
                          readOnly
                          className="bg-light font-monospace"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Number Attribute</Form.Label>
                        <Form.Control
                          type="text"
                          value="telephoneNumber"
                          readOnly
                          className="bg-light font-monospace"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Display Name</Form.Label>
                        <Form.Control
                          type="text"
                          value="%cn"
                          readOnly
                          className="bg-light font-monospace"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Max. Hits (Ergebnisse)</Form.Label>
                        <Form.Control
                          type="text"
                          value="50"
                          readOnly
                          className="bg-light"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>LDAP Version</Form.Label>
                        <Form.Control
                          type="text"
                          value="3"
                          readOnly
                          className="bg-light"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <div className="alert alert-info mt-3 mb-0">
                    <i className="bi bi-info-circle me-2"></i>
                    <strong>Hinweis:</strong> Das LDAP-Telefonbuch zeigt alle Nebenstellen an, bei denen "Im Telefonbuch anzeigen" aktiviert ist (Tab "Basis").
                  </div>
                </Card.Body>
              </Card>
            </Tab>

            {/* Tab: CardDAV-Telefonbuch */}
            <Tab eventKey="carddav" title="CardDAV">
              <Card className="mb-3">
                <Card.Header>
                  <strong><i className="bi bi-phone me-2"></i>CardDAV-Telefonbuch Zugangsdaten</strong>
                </Card.Header>
                <Card.Body>
                  <p className="text-muted mb-3">
                    Verwenden Sie diese Einstellungen, um das CardDAV-Telefonbuch auf Smartphones (iOS, Android) oder Desktop-Anwendungen (macOS, Thunderbird) einzurichten.
                  </p>

                  <div className="alert alert-success mb-3">
                    <i className="bi bi-shield-check me-2"></i>
                    <strong>Verschlüsselte Verbindung:</strong> CardDAV verwendet HTTPS für sichere Übertragung.
                  </div>

                  <Card className="mb-3 border-primary">
                    <Card.Header className="bg-primary text-white">
                      <i className="bi bi-globe me-2"></i>
                      <strong>Server-Einstellungen</strong>
                    </Card.Header>
                    <Card.Body>
                      <Form.Group className="mb-3">
                        <Form.Label>CardDAV-URL</Form.Label>
                        <Form.Control
                          type="text"
                          value={tenantDomain ? `https://${window.location.hostname}/carddav/${tenantDomain}/addressbook/` : `https://${window.location.hostname}/carddav/[DOMAIN]/addressbook/`}
                          readOnly
                          className="bg-light font-monospace"
                        />
                        <Form.Text className="text-muted">
                          Vollständige URL zum CardDAV-Adressbuch
                        </Form.Text>
                      </Form.Group>

                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Server</Form.Label>
                            <Form.Control
                              type="text"
                              value={window.location.hostname}
                              readOnly
                              className="bg-light"
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Port</Form.Label>
                            <Form.Control
                              type="text"
                              value="443 (HTTPS)"
                              readOnly
                              className="bg-light"
                            />
                          </Form.Group>
                        </Col>
                      </Row>

                      <Form.Group className="mb-0">
                        <Form.Label>Pfad</Form.Label>
                        <Form.Control
                          type="text"
                          value={tenantDomain ? `/carddav/${tenantDomain}/addressbook/` : '/carddav/[DOMAIN]/addressbook/'}
                          readOnly
                          className="bg-light font-monospace"
                        />
                      </Form.Group>
                    </Card.Body>
                  </Card>

                  <Card className="mb-3 border-warning">
                    <Card.Header className="bg-warning text-dark">
                      <i className="bi bi-key me-2"></i>
                      <strong>Authentifizierung für diese Nebenstelle</strong>
                    </Card.Header>
                    <Card.Body>
                      {(!formData.extension || !tenantDomain) ? (
                        <div className="alert alert-secondary mb-0">
                          <i className="bi bi-info-circle me-2"></i>
                          Speichern Sie die Nebenstelle, um die CardDAV-Zugangsdaten anzuzeigen.
                        </div>
                      ) : (
                        <>
                          <Row>
                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label>Benutzername</Form.Label>
                                <Form.Control
                                  type="text"
                                  value={`${tenantDomain}_${formData.extension}`}
                                  readOnly
                                  className="bg-light font-monospace"
                                />
                                <Form.Text className="text-muted">
                                  Format: Domain_Nebenstelle
                                </Form.Text>
                              </Form.Group>
                            </Col>
                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label>Passwort</Form.Label>
                                <Form.Control
                                  type="text"
                                  value={formData.password || '(SIP-Passwort)'}
                                  readOnly
                                  className="bg-light"
                                />
                                <Form.Text className="text-muted">
                                  SIP-Passwort dieser Nebenstelle
                                </Form.Text>
                              </Form.Group>
                            </Col>
                          </Row>
                        </>
                      )}
                    </Card.Body>
                  </Card>

                  <hr />

                  <h6 className="mb-3"><i className="bi bi-apple me-2"></i>Einrichtung auf iOS / macOS</h6>
                  <ol className="mb-4">
                    <li>Einstellungen → Kontakte → Accounts → Account hinzufügen → Andere</li>
                    <li>CardDAV-Account hinzufügen auswählen</li>
                    <li>Server: <code>{window.location.hostname}</code></li>
                    <li>Benutzername: <code>{tenantDomain && formData.extension ? `${tenantDomain}_${formData.extension}` : '[DOMAIN]_[NEBENSTELLE]'}</code></li>
                    <li>Passwort: SIP-Passwort eingeben</li>
                    <li>Beschreibung: z.B. "Firmen-Telefonbuch"</li>
                  </ol>

                  <h6 className="mb-3"><i className="bi bi-android2 me-2"></i>Einrichtung auf Android</h6>
                  <ol className="mb-3">
                    <li>DAVx⁵ App aus dem Play Store installieren</li>
                    <li>Neuen Account hinzufügen → Mit URL anmelden</li>
                    <li>URL: <code>{tenantDomain ? `https://${window.location.hostname}/carddav/${tenantDomain}/addressbook/` : `https://${window.location.hostname}/carddav/[DOMAIN]/addressbook/`}</code></li>
                    <li>Zugangsdaten eingeben</li>
                  </ol>

                  <div className="alert alert-info mt-3 mb-0">
                    <i className="bi bi-info-circle me-2"></i>
                    <strong>Hinweis:</strong> CardDAV muss für den Mandanten aktiviert sein (Einstellung in der Mandantenverwaltung).
                  </div>
                </Card.Body>
              </Card>
            </Tab>
          </Tabs>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>Abbrechen</Button>
          <Button variant="primary" type="submit">Speichern</Button>
        </Modal.Footer>
      </Form>
    </Modal>
  )
}

interface CloneModalProps {
  show: boolean
  onHide: () => void
  extension: Extension | null
  onClone: (data: { new_extension: string; new_name: string; new_password?: string }) => void
  existingExtensions: string[]
}

function CloneModal({ show, onHide, extension, onClone, existingExtensions }: CloneModalProps) {
  const [newExtension, setNewExtension] = useState('')
  const [newName, setNewName] = useState('')
  const [newPassword, setNewPassword] = useState('')

  useEffect(() => {
    if (extension) {
      // Suggest next extension number
      const num = parseInt(extension.extension)
      if (!isNaN(num)) {
        let next = num + 1
        while (existingExtensions.includes(String(next))) {
          next++
        }
        setNewExtension(String(next))
      } else {
        setNewExtension('')
      }
      setNewName(`${extension.name} (Kopie)`)
      setNewPassword('')
    }
  }, [extension, existingExtensions])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (existingExtensions.includes(newExtension)) {
      toast.error('Diese Nebenstelle existiert bereits')
      return
    }
    onClone({
      new_extension: newExtension,
      new_name: newName,
      new_password: newPassword || undefined
    })
  }

  if (!extension) return null

  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>Nebenstelle {extension.extension} klonen</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <p className="text-muted">
            Alle Einstellungen von Nebenstelle <strong>{extension.extension}</strong> werden kopiert.
          </p>
          <Form.Group className="mb-3">
            <Form.Label>Neue Nebenstelle *</Form.Label>
            <Form.Control
              type="text"
              inputMode="numeric"
              maxLength={10}
              value={newExtension}
              onChange={(e) => setNewExtension(e.target.value.replace(/\D/g, ''))}
              required
              isInvalid={existingExtensions.includes(newExtension)}
            />
            <Form.Control.Feedback type="invalid">
              Diese Nebenstelle existiert bereits
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Neuer Name *</Form.Label>
            <Form.Control
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              required
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Neues SIP-Passwort</Form.Label>
            <Form.Control
              type="text"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Automatisch generiert wenn leer"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>Abbrechen</Button>
          <Button variant="primary" type="submit" disabled={existingExtensions.includes(newExtension)}>
            <i className="bi bi-copy me-1"></i> Klonen
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  )
}
